import { useState, useRef, useEffect } from 'react';
import S2sEvent from '../helper/s2sEvents';
import { base64ToFloat32Array, processAudioData, resampleFloat32 } from '../helper/audioHelper';
import { useAudioPlayer } from '../helper/audioPlayer';
import { handleBeforeUnload } from 'features/realplays/services/mutations/useUpdateSessionStatus';
import cat from 'assets/cat.jpeg';
import RecordVideoHelper from './RecordVideo.helper';
import { toastFlashMessage } from 'utils';
import { useHistory, useParams } from 'react-router-dom';
import RecordVideoServicesLogic from './RecordVideoServicesLogic';
import { useShouldShowNewRolePlayChanges } from 'features/realplays/RealPlaysHook';

const RecordVideoLogic = ({
  onCancelRecord = () => null,
  onStartRecord = () => null,
  isPublic = false,
  aiProspectOption = false,
}) => {
  const history = useHistory();
  const { uuid, token } = useParams();

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const socketRef = useRef(null);
  const drawIntervalRef = useRef(null);
  const promptNameRef = useRef(null);
  const audioContentNameRef = useRef(null);

  const [isContextModalShowed, setIsContextModalShowed] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [status, setStatus] = useState('Press “Start recording” to begin');
  const [isLoading, setIsLoading] = useState(false);

  const [time, setTime] = useState(0);
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' }); //to display time left

  // Upload finished video to server
  const [previewUrl, setPreviewUrl] = useState(''); //prevURL

  const [cameraError, setCameraError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const handleCameraError = () => setCameraError(true);

  const { active } = useShouldShowNewRolePlayChanges();

  const {
    start: startAudioPlayer,
    stop: stopAudioPlayer,
    playAudio,
    connectMicInput,
    bargeIn,
    getMixedAudioStream,
    getSampleRate,
    isReceivingAudioRef,
  } = useAudioPlayer();

  const {
    assignment_uuid,
    scenario_uuid,
    body,
    session_uuid,
    voice_id,
    persona_name,
    persona_image,
    setIsRecordingScenario,
    tempVideoUrl,
    videoURL,
  } = RecordVideoHelper({ aiProspectOption });

  const {
    startSession,
    isLoadingNormalSession,
    updateSession,
    uploadTempVideo,
    startPublicSession,
    isLoadingPublicSession,
    updatePublicSession,
    uploadPublicTempVideo,
  } = RecordVideoServicesLogic();

  const audioConfig = S2sEvent.DEFAULT_AUDIO_OUTPUT_CONFIG;
  audioConfig.voiceId = voice_id ? voice_id : S2sEvent.voiceOptions[0].value;
  const toolConfig = { tools: [] };

  const onConfirmContextModal = () => {
    setIsContextModalShowed(false);
    prevStartRecording();
  }

  const sendEvent = (event) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(event));
      event.timestamp = Date.now();
    }
  };

  const handleIncomingMessage = (message) => {
    if (!message || !message.event) {
      console.warn('Received invalid message format:', message);
      return;
    }

    const eventType = Object.keys(message?.event)[0];
    const eventData = message.event[eventType];

    if (!eventType || !eventData) {
      console.warn('Malformed message event:', message);
      return;
    }

    switch (eventType) {
      case 'textOutput':
        try {
          const { role, content } = eventData;
          if (role === 'ASSISTANT' && content.startsWith('{')) {
            try {
              const evt = JSON.parse(content);
              if (evt.interrupted === true) bargeIn();
            } catch (e) {
              console.warn('Error parsing interruption event:', e);
            }
          }
        } catch (e) {
          console.error('Error processing text output:', e);
        }

        break;

      case 'audioOutput':
        try {
          if (!eventData.content || typeof eventData.content !== 'string') {
            console.warn('Invalid audio content:', eventData);
            return;
          }
          const audioData = base64ToFloat32Array(eventData.content);
          const targetRate = getSampleRate() || 48000;
          const floatAudio = resampleFloat32(audioData, 24000, targetRate);
          playAudio(floatAudio);
        } catch (error) {
          console.error('Error processing audio:', error);
        }
        break;
      case 'usageEvent':
        console.log('Usage event received:', eventData);
        break;
      default:
        console.warn('Unhandled message type:', eventType, message);
    }
  };

  const socketOnOpenAction = (
    _promptName,
    _textContentName,
    _audioContentName,
    system_prompt,
    inferenceConfig
  ) => {
    sendEvent(S2sEvent.sessionStart(inferenceConfig));
    sendEvent(S2sEvent.promptStartEvent(_promptName, audioConfig, toolConfig));

    sendEvent(S2sEvent.contentStartText(_promptName, _textContentName));
    sendEvent(S2sEvent.textInput(_promptName, _textContentName, system_prompt));
    sendEvent(S2sEvent.contentEnd(_promptName, _textContentName));

    sendEvent(S2sEvent.contentStartAudio(_promptName, _audioContentName));
  };

  const startWebSocket = ({ session_id, system_prompt, inferenceConfig }) => {
    return new Promise((resolve, reject) => {
      if (
        socketRef.current &&
        [WebSocket.OPEN, WebSocket.CONNECTING].includes(socketRef.current.readyState)
      ) {
        resolve();
        return;
      }
      const _promptName = session_id;
      const _textContentName = crypto.randomUUID();
      const _audioContentName = crypto.randomUUID();
      promptNameRef.current = _promptName;
      audioContentNameRef.current = _audioContentName;

      const token = JSON.parse(localStorage.getItem('userInfo') || '{}')?.access || null;
      const wsUrl = isPublic
        ? `${process.env.REACT_APP_BASE_PUBLIC_SOCKET_URL}`
        : `${process.env.REACT_APP_BASE_AI_SOCKET_URL}?token=${token}`;
      socketRef.current = new WebSocket(wsUrl);

      socketRef.current.onopen = () => {
        console.log('WebSocket connected!');
        setStatus('WebSocket connected. Ready to record.');

        socketOnOpenAction(
          _promptName,
          _textContentName,
          _audioContentName,
          system_prompt,
          inferenceConfig
        );
        resolve();
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', JSON.stringify(error, null, 2));
        setStatus(`WebSocket error: ${error.message}`);
        endSession({ status: 'CANCELED' }, false);
        onCancelRecord();
        setIsLoading(false);
        reject(error);
      };

      socketRef.current.onclose = (event) => {
        console.log('WebSocket disconnected');
        socketRef.current = null;
      };

      socketRef.current.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data);
          handleIncomingMessage(event);
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };
    });
  };

  const onLoadStaticCoachImage = (ctx, canvas, staticImg) => {
    if (drawIntervalRef.current) clearInterval(drawIntervalRef.current);

    const drawCover = (ctx, video, width, height) => {
      const videoAspect = video.videoWidth / video.videoHeight;
      const canvasAspect = width / height;

      let renderWidth, renderHeight, x, y;

      if (videoAspect > canvasAspect) {
        // Video más ancho → recortar lados
        renderHeight = height;
        renderWidth = height * videoAspect;
        x = (width - renderWidth) / 2;
        y = 0;
      } else {
        // Video más alto → recortar arriba/abajo
        renderWidth = width;
        renderHeight = width / videoAspect;
        x = 0;
        y = (height - renderHeight) / 2;
      }

      ctx.drawImage(video, x, y, renderWidth, renderHeight);
    };

    drawIntervalRef.current = setInterval(() => {
      const videoEl = webcamRef.current?.video;
      if (!videoEl || videoEl.readyState < 2) return;

      const width = canvas.clientWidth;
      const height = canvas.clientHeight;

      ctx.clearRect(0, 0, width, height); // clean canvas

      // Draw WebCam full size
      //ctx.drawImage(videoEl, 0, 0, width, height);
      drawCover(ctx, videoEl, width, height);

      // background box
      const boxWidth = width * 0.22;
      const boxHeight = height * 0.25;
      const boxX = width - boxWidth - 10; // margin right
      const boxY = 10;
      const borderRadius = boxWidth * 0.05;

      // background box with radio
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(boxX + borderRadius, boxY);
      ctx.lineTo(boxX + boxWidth - borderRadius, boxY);
      ctx.quadraticCurveTo(boxX + boxWidth, boxY, boxX + boxWidth, boxY + borderRadius);
      ctx.lineTo(boxX + boxWidth, boxY + boxHeight - borderRadius);
      ctx.quadraticCurveTo(
        boxX + boxWidth,
        boxY + boxHeight,
        boxX + boxWidth - borderRadius,
        boxY + boxHeight
      );
      ctx.lineTo(boxX + borderRadius, boxY + boxHeight);
      ctx.quadraticCurveTo(boxX, boxY + boxHeight, boxX, boxY + boxHeight - borderRadius);
      ctx.lineTo(boxX, boxY + borderRadius);
      ctx.quadraticCurveTo(boxX, boxY, boxX + borderRadius, boxY);
      ctx.closePath();
      ctx.clip();

      ctx.strokeStyle = '#444';
      ctx.lineWidth = 1;
      ctx.stroke();

      //Draw the image inside the box.
      const aspect = staticImg.width / staticImg.height;
      const boxAspect = boxWidth / boxHeight;

      let drawWidth, drawHeight, imgX, imgY;

      if (aspect > boxAspect) {
        // Imagen más ancha → recortamos lados
        drawHeight = boxHeight;
        drawWidth = boxHeight * aspect;
        imgX = boxX + (boxWidth - drawWidth) / 2;
        imgY = boxY;
      } else {
        // Imagen más alta → recortamos arriba/abajo
        drawWidth = boxWidth;
        drawHeight = boxWidth / aspect;
        imgX = boxX;
        imgY = boxY + (boxHeight - drawHeight) / 2;
      }

      ctx.drawImage(staticImg, imgX, imgY, drawWidth, drawHeight);

      // User Text
      ctx.font = `bold ${Math.max(9, boxHeight * 0.09)}px Open Sans`;
      ctx.fillStyle = 'white';
      ctx.fillText(persona_name, boxX + 6, boxY + boxHeight - 8);

      // Animated waveform microphone
      const micBgColor = '#1976d2';
      const micBarColor = '#FFFFFF'; // white bars

      const micCenterX = boxX + boxWidth / 2;
      const micBottomY = boxY + boxHeight - 6;
      const micRadius = boxWidth * 0.07;

      // Draw background circle
      ctx.beginPath();
      ctx.arc(micCenterX, micBottomY - micRadius, micRadius, 0, 2 * Math.PI);
      ctx.fillStyle = micBgColor;
      ctx.fill();
      ctx.closePath();

      // Bar parameters
      const barCount = 3;
      const barSpacing = micRadius * 0.23;
      const barWidth = micRadius * 0.2;
      const maxBarHeight = micRadius * 0.6;

      // Animated heights (simple oscillations)
      const time = Date.now() / 200;
      const barHeights = Array.from({ length: barCount }, (_, i) => {
        if (!isReceivingAudioRef.current) return micRadius * 0.4;
        const phase = i * 1.1;
        return micRadius * 0.3 + Math.abs(Math.sin(time + phase)) * maxBarHeight;
      });

      // Draw bars
      for (let i = 0; i < barCount; i++) {
        const barHeight = barHeights[i];
        const x =
          micCenterX - ((barCount - 1) * (barWidth + barSpacing)) / 2 + i * (barWidth + barSpacing);
        const y = micBottomY - micRadius - barHeight / 2;

        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x, y, barWidth, barHeight, 1.5);
        else ctx.fillRect(x, y, barWidth, barHeight);
        ctx.fillStyle = micBarColor;
        ctx.fill();
        ctx.closePath();
      }

      // "Prospect" label
      const iaText = 'Prospect';
      ctx.font = `bold ${Math.max(9, boxHeight * 0.08)}px Arial`;
      const textMetrics = ctx.measureText(iaText);
      const paddingX = 6;
      const tagWidth = textMetrics.width + paddingX * 2;
      const tagHeight = boxHeight * 0.16;
      const tagX = boxX + boxWidth - tagWidth - 6;
      const tagY = boxY + boxHeight - tagHeight - 6;

      ctx.beginPath();
      ctx.moveTo(tagX + 4, tagY);
      ctx.lineTo(tagX + tagWidth - 4, tagY);
      ctx.quadraticCurveTo(tagX + tagWidth, tagY, tagX + tagWidth, tagY + 4);
      ctx.lineTo(tagX + tagWidth, tagY + tagHeight - 4);
      ctx.quadraticCurveTo(
        tagX + tagWidth,
        tagY + tagHeight,
        tagX + tagWidth - 4,
        tagY + tagHeight
      );
      ctx.lineTo(tagX + 4, tagY + tagHeight);
      ctx.quadraticCurveTo(tagX, tagY + tagHeight, tagX, tagY + tagHeight - 4);
      ctx.lineTo(tagX, tagY + 4);
      ctx.quadraticCurveTo(tagX, tagY, tagX + 4, tagY);
      ctx.closePath();
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();

      ctx.fillStyle = '#193448';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(iaText, tagX + tagWidth / 2, tagY + tagHeight / 2);

      ctx.restore();

      //Draw you label inside the box.
      ctx.font = `bold ${Math.max(10, height * 0.025)}px Open Sans`;
      ctx.fillStyle = 'white';
      ctx.fillText('You', 10, height - 10);
    }, 1000 / 30);
  };

  const reset = () => {
    setTime(0);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
  };

  const mediaRecorderStop = () => {
    const videoBlob = new Blob(videoChunksRef.current, { type: 'video/webm' });
    const blobUrl = URL.createObjectURL(videoBlob);
    onRecordingSuccess(blobUrl);
  };

  const onRecordingSuccess = async (blobUrl) => {
    setPreviewUrl(blobUrl);
    setStatus('Recording complete');
    const file = await fetch(blobUrl)
      .then((r) => r.blob())
      .then(
        (blobFile) =>
          new File([blobFile], `recording.mp4`, {
            type: 'video/mp4',
          })
      );
    isPublic ? uploadPublicTempVideo({ file }) : uploadTempVideo({ file });
  };

  const prevStartRecording = () => {
    reset();
    isPublic
      ? startPublicSession(
          { assignment_uuid, scenario_uuid, body },
          {
            onSuccess: (response) => {
              startRecording({
                session_id: response?.session_id,
                system_prompt: response?.system_prompt,
                inferenceConfig: response?.inference_config,
              });
            },
            onError: () => {
              onCancelRecord();
              toastFlashMessage(
                'We couldn’t create a session for this interaction. Please try again later.',
                'error'
              );
            },
          }
        )
      : startSession(
          { assignment_uuid, scenario_uuid, body },
          {
            onSuccess: (response) => {
              startRecording({
                session_id: response?.session_id,
                system_prompt: response?.system_prompt,
                inferenceConfig: response?.inference_config,
              });
            },
            onError: () => {
              onCancelRecord();
              toastFlashMessage(
                'We couldn’t create a session for this interaction. Please try again later.',
                'error'
              );
            },
          }
        );
  };

  const handleError = (message, error) => {
    console.error(message, error);
    setStatus(`Error: ${message}`);
    if (socketRef.current) {
      sendEndEvents();
      socketRef.current.close();
      socketRef.current = null;
    }
    if (session_uuid) endSession({ status: 'CANCELED' }, false);
    setIsLoading(false);
    reset();
    setTimeout(() => onCancelRecord(), 150);
  };

  const startRecording = async ({ session_id, system_prompt, inferenceConfig }) => {
    setIsLoading(true);
    setStatus('Preparing recording...');

    try {
      setStatus('Connecting to the server....');
      await startWebSocket({ session_id, system_prompt, inferenceConfig });

      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        setStatus('Error: Could not connect to the server');
        setIsLoading(false);
        return;
      }

      setStatus('Initializing audio...');
      try {
        await startAudioPlayer();
      } catch (audioError) {
        setAudioError(true);
        handleError('Could not initialize audio', audioError);
        return;
      }

      setStatus('Preparing recording...'); //camera and mic
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: isAudioOn
            ? { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            : false,
        });

        if (webcamRef.current) webcamRef.current.video.srcObject = stream;
      } catch (camError) {
        handleError('Could not access camera/microphone', camError);
        return;
      }

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;

      const resizeCanvas = () => {
        const width = canvas.clientWidth;
        const height = canvas.clientHeight;

        canvas.width = width * dpr;
        canvas.height = height * dpr;

        // Reset transform y escalar por DPR
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      // Draw continuously on the canvas
      const staticImg = new Image();
      staticImg.crossOrigin = 'anonymous';
      staticImg.src = persona_image || cat;

      staticImg.onload = () => onLoadStaticCoachImage(ctx, canvas, staticImg);

      // Configure AudioContext to process audio
      if (isAudioOn) {
        connectMicInput(stream, (inputBuffer) => {
          const inputRate = getSampleRate() || 48000;
          const audioData = processAudioData(inputBuffer, inputRate, 16000);

          const s2sEvent = S2sEvent.audioInput(
            promptNameRef.current,
            audioContentNameRef.current,
            audioData
          );
          sendEvent(s2sEvent);
        });
      }
      // Combine video + mixed audio (mic + coach voice) + stating image

      const canvasStream = canvas.captureStream(30);
      const mixedAudioStream = getMixedAudioStream();

      const mixedStream = new MediaStream([
        ...canvasStream.getVideoTracks(),
        ...mixedAudioStream.getAudioTracks(),
      ]);

      let options = { mimeType: 'video/webm;codecs=vp8,opus' };

      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm;codecs=vp8' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
      }
      mediaRecorderRef.current = new MediaRecorder(mixedStream, options);
      videoChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) videoChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start(100);

      setIsRecording(true);
      setIsLoading(false);
      setStatus('recording');
    } catch (unexpectedError) {
      handleError('Unexpected error when starting recording', unexpectedError);
    }
  };

  const sendEndEvents = () => {
    sendEvent(S2sEvent.contentEnd(promptNameRef.current, audioContentNameRef.current));
    sendEvent(S2sEvent.promptEnd(promptNameRef.current));
    sendEvent(S2sEvent.sessionEnd());
  };

  const stopRecording = (canceled = false, reStart = false) => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = () => {
        if (!canceled) mediaRecorderStop();
      };
      mediaRecorderRef.current.stop();
    }

    stopAudioPlayer();
    clearInterval(drawIntervalRef.current);

    if (socketRef.current) {
      sendEndEvents();
      socketRef.current.close();
      socketRef.current = null;
    }

    if (canceled) endSession({ status: 'CANCELED' }, reStart);

    if (webcamRef.current?.video?.srcObject)
      webcamRef.current.video.srcObject.getTracks().forEach((track) => track.stop());

    setIsRecording(false);
    setStatus('Recording stopped');
  };

  const endSession = (body, reStar = false) => {
    isPublic
      ? updatePublicSession(
          { assignment_uuid, scenario_uuid, session_uuid, body },
          {
            onSuccess: () => {
              if (reStar) prevStartRecording();
            },
          }
        )
      : updateSession(
          { assignment_uuid, scenario_uuid, session_uuid, body },
          {
            onSuccess: () => {
              if (reStar) prevStartRecording();
            },
          }
        );
  };

  const toggleAudio = () => setIsAudioOn(!isAudioOn);

  const onRestart = () => {
    reset();
    stopRecording(true, true);
  };

  const onDelete = () => {
    stopRecording(true, false);
    onCancelRecord();
  };

  const onLeavePage = () => stopRecording(true, false);

  const onReRecord = () => {
    endSession({ status: 'CANCELED' });
    onCancelRecord();
    setTimeout(() => onStartRecord(), 100);
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    let interval = null;
    if (status === 'recording') interval = setInterval(() => setTime((t) => t + 1), 1000);
    else clearInterval(interval);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (time >= 300) stopRecording(false, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  useEffect(() => {
    if (time === 270) {
      setToast({ open: true, message: '30 seconds remaining', type: 'warning' });
    } else if (time === 290) {
      setToast({ open: true, message: 'Only 10 seconds left!', type: 'error' });
    }
  }, [time]);

  useEffect(() => {
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsRecordingScenario(isRecording);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const goingToSubmit = isPublic
        ? location.pathname === `/public-roleplay/${token}/submit`
        : location.pathname === `/realplay/${uuid}/submit`;
      if (
        !isRecording &&
        session_uuid !== '' &&
        tempVideoUrl !== '' &&
        (videoURL === null || videoURL === '') &&
        !goingToSubmit
      ) {
        console.log('cancel');
        endSession({ status: 'CANCELED' });
      }
    });

    return () => {
      unlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isRecording, session_uuid, tempVideoUrl, videoURL, uuid]);

  useEffect(() => {
    const validUUIDs =
      assignment_uuid &&
      assignment_uuid !== '' &&
      scenario_uuid &&
      scenario_uuid !== '' &&
      session_uuid &&
      session_uuid !== '';
    const tempVideoWithoutVideo = tempVideoUrl !== '' && !videoURL;

    if (!validUUIDs || !tempVideoWithoutVideo) return;

    const listener = () => handleBeforeUnload(assignment_uuid, scenario_uuid, session_uuid);
    window.addEventListener('beforeunload', listener);

    return () => {
      window.removeEventListener('beforeunload', listener);
    };
  }, [assignment_uuid, scenario_uuid, session_uuid, tempVideoUrl, videoURL]);

  return {
    isAudioOn,
    isRecording,
    isLoading,
    time,
    previewUrl,
    webcamRef,
    canvasRef,
    toggleAudio,
    stopRecording,
    prevStartRecording,
    isLoadingSession: isPublic ? isLoadingPublicSession : isLoadingNormalSession,
    onRestart,
    onDelete,
    onReRecord,
    onLeavePage,
    toast,
    handleCloseToast,
    cameraError,
    handleCameraError,
    audioError,
    onConfirmContextModal,
    isContextModalShowed,
    rolePlayType: body.type,
    scenario: body.scenario.name,
    customer_context: body.customer_context,
    showNewChanges: active,
  };
};

export default RecordVideoLogic;
